import { Form } from 'antd';
import { Trans } from 'react-i18next';
import { getGlobalSettings } from 'app/appState';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { Box } from 'entities/Box';
import { ContractInfo } from 'entities/Contract';
import React, { FC, memo, useCallback, useEffect, useLayoutEffect, useMemo, useState, ReactNode } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Link, useNavigate } from 'react-router-dom';
import { Checkbox } from 'shared/ui/Checkbox';
import { useScrollToElement } from 'shared/utils/hooks/useScrollToElement';
import { WarehouseForBooking } from 'entities/Warehouse';
import { AppRoutes } from 'app/config/routerConfig/types';
import { Button } from 'shared/ui/Button';
import { BookingDocumentsNames } from 'app/appState/model/types';
import { BookAndPayButton } from 'widgets/PaymentForm/ui/BookAndPayButton';
import { PaymentProcessor, PaymentMethod, PaymentMethodSetting } from 'entities/Payment';
import { ModernRadioButtonGroup, RadioOptionTheme } from 'shared/ui/ModernRadioButtonGroup';
import { getLoggedUserData } from 'entities/User';
import { ReserveBox } from 'features/ReserveBox';
import { GratitudeModalType, useOpenGratitudeModal } from 'features/GratitudeModal';
import { STARTED_BOOKING_DATA_KEY } from 'pages/BookingPage/utils/constants';
import { InfoTag } from 'shared/ui/InfoTag';

interface ModernPaymentFormProps {
  contractInfo: ContractInfo;
  boxInfo: Nullable<Box>;
  warehouseInfo: Nullable<WarehouseForBooking>;
  onCompleteStep: () => void;
}

export const ModernPaymentForm: FC<ModernPaymentFormProps> = memo((props) => {
  const { boxInfo, warehouseInfo, contractInfo, onCompleteStep } = props;

  const { t } = useAppTranslation(['booking', 'common']);
  const navigate = useNavigate();

  const { openGratitudeModal } = useOpenGratitudeModal();

  const [termsAgreement, setTermsAgreement] = useState<boolean>(false);
  const [isAutopaymentEnabled, setAutopaymentEnabled] = useState<boolean>(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(PaymentMethod.ONLINE_PAYMENT);

  const { scrollToElement, scrollRef } = useScrollToElement('center', 500);

  useLayoutEffect(() => {
    scrollToElement();
  }, [scrollToElement]);

  useEffect(() => {
    const watchAutoPaymentStatus = (): void => {
      switch (paymentMethod) {
        case PaymentMethod.ONLINE_PAYMENT:
          setAutopaymentEnabled(true);
          break;
        case PaymentMethod.BANK_TRANSFER:
          setAutopaymentEnabled(false);
          break;
        case PaymentMethod.OTHER:
          setAutopaymentEnabled(false);
          break;
      }
    };

    watchAutoPaymentStatus();
  }, [paymentMethod]);

  const globalSettings = useAppSelector(getGlobalSettings);
  const loggedUserData = useAppSelector(getLoggedUserData);

  React.useEffect(() => {
    console.log(globalSettings?.publicPaymentMethods);
  }, [globalSettings?.publicPaymentMethods]);

  const boxUsageRulesDocument = useMemo(
    () => globalSettings?.bookingDocuments.find(({ docName }) => docName === BookingDocumentsNames.RULES),
    [globalSettings?.bookingDocuments],
  );

  const boxRentalAgreementTemplate = useMemo(
    () => globalSettings?.bookingDocuments.find(({ docName }) => docName === BookingDocumentsNames.BOX_RENTAL_AGREEMENT),
    [globalSettings?.bookingDocuments],
  );

  const toggleAutopayment = useCallback((): void => {
    setAutopaymentEnabled((prevState) => !prevState);
  }, []);

  const getPaymentMethodOption = (
    paymentMethodSetting: PaymentMethodSetting,
  ): { value: string; label: ReactNode; theme?: RadioOptionTheme } => {
    const detaultOtherOption = {
      label: t('Other'),
      value: PaymentProcessor.OTHER,
    };

    const mapper: Partial<Record<PaymentMethod, { value: string; label: ReactNode; theme?: RadioOptionTheme }>> = {
      [PaymentMethod.ONLINE_PAYMENT]: {
        label: (
          <div className="flex items-center justify-between w-full">
            <span>{t('Online payment')}</span>
            <div className="flex flex-wrap items-center gap-2">
              {paymentMethodSetting?.imageUrl && <img src={paymentMethodSetting.imageUrl} className="h-5 w-auto ml-3" />}
            </div>
          </div>
        ),
        value: PaymentMethod.ONLINE_PAYMENT,
      },
      [PaymentMethod.BANK_TRANSFER]: {
        label: t('Bank transfer'),
        value: PaymentMethod.BANK_TRANSFER,
      },
      [PaymentMethod.OTHER]: {
        label: t('Other (You can pay later using one of the available payment methods)'),
        value: PaymentMethod.OTHER,
      },
    };

    return mapper[paymentMethodSetting.name] || detaultOtherOption;
  };

  const paymentMethodsOptions = globalSettings?.publicPaymentMethods
    .map((paymentMethodSetting: PaymentMethodSetting) => getPaymentMethodOption(paymentMethodSetting))
    .filter(Boolean);

  const toggleTermsAgreement = useCallback((): void => {
    setTermsAgreement((prevState) => !prevState);
  }, []);

  const handleOpenGratitudeModal = useCallback((): void => {
    openGratitudeModal({
      description: t(
        'Our company values each and every customer. If you have any questions or feedback, please don`t hesitate to reach out!',
      ),
      type: GratitudeModalType.RESERVATION,
      buttonId: 'reservation id',
    });
  }, [openGratitudeModal, t]);

  const onReservationSuccess = useCallback(() => {
    localStorage.removeItem(STARTED_BOOKING_DATA_KEY);

    navigate(AppRoutes.MY_BOXES);

    handleOpenGratitudeModal();
  }, [handleOpenGratitudeModal, navigate]);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg p-6">
        <div ref={scrollRef} className="font-semibold text-24 mb-6">
          {t('Payment')}
        </div>
        <Form>
          <div className="mb-4">
            <ModernRadioButtonGroup
              options={paymentMethodsOptions || []}
              value={paymentMethod}
              onChange={(val) => {
                setPaymentMethod(val as PaymentMethod);
              }}
              direction="vertical"
              withBorder
              optionType="default"
            />
          </div>

          {/* External descriptive content */}
          {globalSettings?.publicPaymentMethods?.find((m) => m.name === paymentMethod)?.isAutoPaymentAllowed && (
            <>
              <div className="font-semibold text-lg">{t('Auto pay')}</div>
              <div className="mb-2 ml-4">
                {t('Turn on the automatic payment feature so you don`t miss payments. You can turn it off at any time!')}
              </div>
              <div className="ml-4 mb-3">
                <Checkbox name="isAutopayment" checked={isAutopaymentEnabled} onChange={toggleAutopayment}>
                  {t('Allow autopayment')}
                </Checkbox>
              </div>
            </>
          )}
          <div className="mt-2 mb-6">
            <Checkbox name="termsAgreement" checked={termsAgreement} onChange={toggleTermsAgreement}>
              <div className="flex items-center">{t('I have read the following additions to the contract and accept them in full:')}</div>
              {boxRentalAgreementTemplate && (
                <div className="mt-2">
                  <Link
                    key={boxRentalAgreementTemplate.link}
                    className="text-accent text-md font-normal underline"
                    to={boxRentalAgreementTemplate.link}
                    target="_blank"
                  >
                    {t(boxRentalAgreementTemplate.docName)}
                  </Link>
                </div>
              )}
              {boxUsageRulesDocument && (
                <div className="mt-2">
                  <Link
                    key={boxUsageRulesDocument.link}
                    className="text-accent text-md font-normal underline"
                    to={boxUsageRulesDocument.link}
                    target="_blank"
                  >
                    {t(boxUsageRulesDocument.docName)}
                  </Link>
                </div>
              )}
            </Checkbox>
          </div>

          <div className="my-5">
            <InfoTag>
              <Trans
                t={t}
                i18nKey="Dear clients! <0 /> <0 />To verify your identity, please upload documents to your personal account. Please note that document verification is only performed by the administrator between 9:00 AM and 6:00 PM.<0 /><0 />Access to payment and further use of the box will be granted only after your documents have been successfully verified."
                components={[<br key="0" />]}
              />
            </InfoTag>
          </div>

          <div className="flex space-x-3">
            <ReserveBox
              action={
                <Button containerClassName="w-full" size="medium" theme="primary-outlined" isDisabled={!termsAgreement}>
                  {t('Reserve')}
                </Button>
              }
              boxId={boxInfo?.boxId}
              contractOptions={contractInfo}
              warehouseId={warehouseInfo?.warehouseId}
              onSuccess={onReservationSuccess}
              userId={loggedUserData?.userId}
            />

            <BookAndPayButton
              size="medium"
              isDisabled={!termsAgreement}
              loggedUserData={loggedUserData}
              contractInfo={contractInfo}
              boxInfo={boxInfo}
              warehouseInfo={warehouseInfo}
              globalSettings={globalSettings}
              isAutopaymentEnabled={isAutopaymentEnabled}
              paymentMethod={paymentMethod}
              onCompleteStep={onCompleteStep}
            />
          </div>
        </Form>
      </div>
    </div>
  );
});
